import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, switchMap } from 'rxjs/operators';
import { Like } from '@modules/common/types/like';
import { Section } from '@modules/common/types/section';
import { DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { DragNDropService } from '@modules/drag-n-drop/services/drag-n-drop.service';
import { Project } from '@modules/tasks/types/project';
import { Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { BoardService } from '@modules/tasks/services/board.service';

@Component({
  selector: 'app-project-tasks-list-section',
  templateUrl: './project-tasks-list-section.component.html',
  host: { class: 'block' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectTasksListSectionComponent {
  public readonly project = input<Project>();
  public readonly section = input<Section>();
  public readonly filters = input<Like<TasksFilters>>({});
  public readonly canMoveUp = input(true);
  public readonly canMoveDown = input(true);

  public readonly clickTask = output<Task>();
  public readonly openTask = output<Task>();
  public readonly doubleClickTask = output<Task>();
  public readonly openSubtask = output<Task>();

  private readonly boardService = inject(BoardService);
  private readonly dndService = inject(DragNDropService);

  protected readonly sectionForm = computed(() => this.section()?.asFormGroup());
  private readonly titleInputRef = viewChild<ElementRef<HTMLInputElement>>('titleInput');

  constructor() {
    // A section just added from "Add new section" puts the cursor in its name, its default title selected.
    afterNextRender(() => {
      if (!this.boardService.claimSectionFocus(this.section()?.id)) return;
      const input = this.titleInputRef()?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  // Every project column is rendered under every section (empty ones included), so
  // each stays a stable drop target and the List mirrors the board's column grid.
  protected readonly columns = toSignal(this.boardService.getColumns(), { initialValue: [] });

  protected readonly collapsed = toSignal(
    toObservable(this.section).pipe(
      filter(Boolean),
      switchMap((section) => this.boardService.getSectionCollapsed(section.id)),
    ),
    { initialValue: false },
  );

  private readonly draggedType = toSignal(this.dndService.getDraggingDataChanges().pipe(map((data) => data?.type)));
  // Only a task drag can be dropped here, so drive the drop affordance off the type,
  // not the global drag state (which fires for any dragged item app-wide).
  protected readonly dragging = computed(() => this.draggedType() === DragDataTypes.task);

  protected handleBlur(): void {
    const form = this.sectionForm();
    const titleControl = form?.controls.title;
    if (!form || !titleControl.dirty || (titleControl.value ?? '').trim() === '') {
      return;
    }

    this.boardService.updateSection(Section.fromFormGroup(form));
  }

  protected toggleCollapse(): void {
    const section = this.section();
    if (section) {
      this.boardService.setSectionCollapsed(section.id, !this.collapsed());
    }
  }
}
